import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { endpointDashboardQuery, type EndpointTopAlarm } from '../api/inventoree'
import { moduleOf, shellQuery } from '../api/shell'
import { OdbBoard, OdbCard } from '../dashboard/OdbCard'
import { usePageStyles } from '../ui/usePageStyles'
import {
  ChartEmpty,
  DoughnutChart,
  ENDPOINT_COLORS,
  minutesLabel,
  scoreColor,
  scorePercent,
  StackedBarChart,
  VerticalBarChart,
} from './EndpointCharts'

/**
 * Inventoree::endpoint_performance_dashboard, pe desenul de tablou.
 *
 * -----------------------------------------------------------------------------
 * ADRESA E INVENTOREE, BARA E OPTEEMUM
 * -----------------------------------------------------------------------------
 *
 * Ecranul vechi isi pune `appModule = 'opteemum'`, desi sta la o adresa
 * Inventoree - de-aia `MODULE_BY_URI` din `api/shell.ts` leaga TOATA adresa
 * `inventoree/endpoint_performance_dashboard` de Opteemum. Randul ala nu se
 * atinge: fara el pagina s-ar desena sub bara Inventoree, adica sub alt meniu
 * decat cel din care omul a apasat. Aceeasi socoteala si aici: `moduleOf(...)`
 * da Opteemum, el se cere de la shell, si placutele cardurilor poarta culoarea
 * Opteemum.
 *
 * -----------------------------------------------------------------------------
 * DE CE E PE `od_dashboard.css` SI NU PE PANOURI BOOTSTRAP
 * -----------------------------------------------------------------------------
 *
 * Fiindca de doua zile tablourile portalului sunt acolo: cel central si cele de
 * modul. Zona are fundalul ei aproape alb, cardul fara chenar cu colturi de
 * 14px, cifra mare si placuta colorata a modulului - vezi capul foii pentru de
 * ce a fost o RUPTURA si nu o indreptare. Un tablou lasat pe `panel
 * panel-default` ar fi fost, de la o pagina la alta, al doilea limbaj pentru
 * acelasi lucru.
 *
 * Ce se schimba fata de ecranul vechi, cu buna stiinta:
 *
 *   - panourile devin carduri in GRILA de patru coloane (`.odb`), cu aceleasi
 *     latimi ca inainte: o celula unde era `col-md-3`, doua unde era `col-md-6`.
 *     Fiecare grafic tine doua randuri de grila, fiindca intr-unul singur (cel
 *     putin 200px) nu incape un desen cu axa si etichete;
 *   - titlul "Performance score" nu mai e ROSU. In zona asta culoarea inseamna
 *     STARE, si un titlu rosu deasupra unui scor bun spunea invers decat cifra
 *     de sub el;
 *   - panoul negru "Number of alarms" se desface in patru carduri de cifra,
 *     unul de fiecare fel de alarma, cu rigla care arata cat din total inseamna.
 *     Totalul nu se pierde: il poarta cardul cu gogoasa, in capul lui. Cinci
 *     cifre albe pe negru, cu bare de progres care spuneau acelasi lucru a doua
 *     oara, erau ultima bucata de tablou din 2014 ramasa pe ecran.
 *
 * Ce NU se schimba:
 *
 *   - GRAFICELE RAMAN DESENATE DE MANA, in `EndpointCharts.tsx`. Portalul intra
 *     in audit bancar, amCharts a fost scos cu doua probe incuiate, si o
 *     biblioteca de grafice nu se intoarce pe usa din dos a unei mutari de
 *     desen. Formele si culorile sunt tot cele din `endpoint_dashboard.js`;
 *   - perioada. Porneste pe ziua de azi, ca `reportrange`, si are aceleasi sase
 *     presetari - acum pastilele din bara, ca ferestrele de timp ale celorlalte
 *     tablouri.
 *
 * -----------------------------------------------------------------------------
 * MARGINEA PAGINII SE MUTA, SI ASTA E O HOTARARE
 * -----------------------------------------------------------------------------
 *
 * Inainte: randul de sus la 5px de marginea zonei (`#page-content` are 20, iar
 * `.row` isi scade 15), panourile la 20px - masurat fata de portal, si potrivit.
 *
 * Acum: totul la 16px. `.odb-page` isi anuleaza cei 20 ai lui `#page-content` si
 * pune 16 ai ei, ca pe toate celelalte tablouri. Nu e o scapare: pretul intrarii
 * in zona e ca ecranul sa se asaze ca ea, iar cine vine de pe tabloul Opteemum
 * incoace vede cardurile pornind din acelasi loc.
 *
 * NU e schimbarea respinsa aseara. Aceea ar fi dus pagina la 20px INAUNTRUL
 * desenului vechi - adica 15px mai departe de portal, fara nimic in schimb.
 */

/** Ziua locala ca 'YYYY-MM-DD' - `toISOString` ar da-o pe cea de la Greenwich. */
export function isoDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function shiftDays(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return isoDay(d)
}

function monthEdge(monthsBack: number, edge: 'start' | 'end'): string {
  const now = new Date()
  const d = edge === 'start'
    ? new Date(now.getFullYear(), now.getMonth() - monthsBack, 1)
    : new Date(now.getFullYear(), now.getMonth() - monthsBack + 1, 0)
  return isoDay(d)
}

/** Aceleasi `ranges` pe care le dadea daterangepicker in endpoint_dashboard.js. */
export function endpointPresets(): { label: string; from: string; to: string }[] {
  return [
    { label: 'Today', from: shiftDays(0), to: shiftDays(0) },
    { label: 'Yesterday', from: shiftDays(-1), to: shiftDays(-1) },
    { label: 'Last 7 Days', from: shiftDays(-6), to: shiftDays(0) },
    { label: 'Last 30 Days', from: shiftDays(-29), to: shiftDays(0) },
    { label: 'This Month', from: monthEdge(0, 'start'), to: monthEdge(0, 'end') },
    { label: 'Last Month', from: monthEdge(1, 'start'), to: monthEdge(1, 'end') },
  ]
}

/**
 * Ziua aleasa se trimite ca interval de ceas, nu ca data goala.
 *
 * Coloanele numarate sunt `created_at`, adica momente; `created_at <= '2026-09-08'`
 * inseamna pentru MySQL miezul noptii, deci ziua de azi ar fi iesit mereu goala.
 * De-aia capatul de sus urca la 23:59:59 al zilei alese.
 */
function startOf(day: string): string {
  return day === '' ? '' : `${day} 00:00:00`
}

function endOf(day: string): string {
  return day === '' ? '' : `${day} 23:59:59`
}

/** Eticheta din graficul stivuit: "3: NUME (42.5%)", procentul dintr-o zi de 8 ore. */
export function deviceBarLabel(index: number, name: string, minutes: number): string {
  return `${index + 1}: ${name} (${scorePercent(minutes / 60)}%)`
}

export function EndpointPerformancePage() {
  /* Pornim pe ziua de azi, ca `reportrange` in ecranul vechi. */
  const today = useMemo(() => isoDay(new Date()), [])
  const [fromDay, setFromDay] = useState(today)
  const [toDay, setToDay] = useState(today)
  const from = startOf(fromDay)
  const to = endOf(toDay)
  const presets = useMemo(() => endpointPresets(), [])

  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(['css/od_dashboard.css'], shell.data?.version ?? '')

  const page = useQuery(endpointDashboardQuery(from, to))

  /* Aceeasi propozitie peste tot: golul e al perioadei, nu al ecranului. */
  const period = fromDay === toDay ? fromDay : `${fromDay} - ${toDay}`

  const bar = (
    <div className="odb-bar-top">
      <h1 className="odb-name">Endpoint Performance</h1>
      <nav className="odb-views" aria-label="Date range">
        {presets.map((r) => (
          <button
            type="button"
            key={r.label}
            className={`odb-view${r.from === fromDay && r.to === toDay ? ' is-on' : ''}`}
            onClick={() => {
              setFromDay(r.from)
              setToDay(r.to)
            }}
          >
            {r.label}
          </button>
        ))}
      </nav>
      {/*
        `form-inline`, nu numai `.odb-bar-right`: `form-control` din Bootstrap e
        `display: block; width: 100%`, iar intr-un rand flex asta da doua casute
        intinse cat tine bara. `form-inline` le face `inline-block`, cu latimea
        lor.
      */}
      <div className="odb-bar-right form-inline">
        <label htmlFor="epFrom">
          <i className="fa fa-calendar" aria-hidden="true"></i> From
        </label>
        <input
          type="date"
          id="epFrom"
          className="form-control input-sm"
          value={fromDay}
          max={toDay || undefined}
          onChange={(e) => setFromDay(e.target.value)}
        />
        <label htmlFor="epTo">To</label>
        <input
          type="date"
          id="epTo"
          className="form-control input-sm"
          value={toDay}
          min={fromDay || undefined}
          onChange={(e) => setToDay(e.target.value)}
        />
      </div>
    </div>
  )

  if (page.error) {
    return (
      <div className="odb-page">
        {bar}
        <div className="odb-empty">
          <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> Something went wrong: {page.error.message}
        </div>
      </div>
    )
  }
  if (!page.data) {
    return (
      <div className="odb-page">
        {bar}
        <div className="odb-empty">Reading the alarms for {period}…</div>
      </div>
    )
  }

  const data = page.data
  const alarms = data.alarms
  const scores = data.performance_score
  const top = data.top_alarms

  return (
    <OdbBoard bar={bar}>
      <OdbCard module="opteemum" icon="fa-tachometer-alt" title="Performance score" w={1} h={2} id="performanceScore">
        {(height) =>
          scores.length === 0 ? (
            <ChartEmpty>
              No alarm hours recorded for {period}. The score is drawn from endpoint alarms, and there were none.
            </ChartEmpty>
          ) : (
            <VerticalBarChart
              height={height}
              items={scores.map((s) => {
                const pct = scorePercent(Number(s.performance))
                return { label: s.Denumire, value: pct, color: scoreColor(pct), tip: `${pct} points` }
              })}
            />
          )
        }
      </OdbCard>

      <OdbCard module="opteemum" icon="fa-layer-group" title="Top of alarms on devices" w={2} h={2} id="topAlarmsDevice">
        {(height) =>
          top.length === 0 ? (
            <ChartEmpty>No device raised an alarm in {period}.</ChartEmpty>
          ) : (
            <StackedBarChart
              height={height}
              rows={top.map((s, i) => ({
                label: deviceBarLabel(i, s.Denumire, Number(s.number)),
                parts: [
                  { key: 'processor', value: Number(s.processor), color: ENDPOINT_COLORS.processor },
                  { key: 'ram', value: Number(s.ram), color: ENDPOINT_COLORS.ram },
                  { key: 'swap', value: Number(s.swap), color: ENDPOINT_COLORS.swap },
                  { key: 'services', value: Number(s.services), color: ENDPOINT_COLORS.services },
                ],
              }))}
            />
          )
        }
      </OdbCard>

      {/*
        "Affected performance" cerea al DOILEA drum la server - capatul
        `/endpoint-performance/table`, care rula exact acelasi `topAlarms` cu
        aceiasi doi parametri si intorcea aceleasi randuri ca `top_alarms` de
        deasupra. In portal existau doua capete fiindca acolo tabelul era un
        DataTable cu ajaxul lui; aici randurile sunt deja in mana. Doua cereri
        pentru acelasi raspuns inseamna si de doua ori interogarea cea mai grea a
        ecranului, si inca un loc din care ecranul poate ramane gol.
      */}
      <OdbCard module="opteemum" icon="fa-hourglass-half" title="Affected performance" w={1} h={2} id="topAlarmsDeviceLine">
        {(height) => <AffectedRows rows={top} height={height} period={period} />}
      </OdbCard>

      <OdbCard module="opteemum" icon="fa-globe" title="Average website load time" w={2} h={2} id="websiteLoadTime">
        {(height) =>
          data.website_load_time.length === 0 ? (
            <ChartEmpty>
              No page load times were measured in {period}. The figure comes from the agent&apos;s address probe, and nothing
              was recorded.
            </ChartEmpty>
          ) : (
            <VerticalBarChart
              height={height}
              items={data.website_load_time.map((s) => ({
                label: `${s.Denumire} ${s.address}`,
                value: Number(s.avg_loadTime),
                color: '#32A751',
                tip: `${Number(s.avg_loadTime)}ms`,
              }))}
            />
          )
        }
      </OdbCard>

      <OdbCard module="opteemum" icon="fa-network-wired" title="Response time LAN" w={1} h={2} id="lanResponses">
        {(height) =>
          data.lan_response.length === 0 ? (
            <ChartEmpty>No LAN response was measured in {period}.</ChartEmpty>
          ) : (
            <VerticalBarChart
              height={height}
              items={data.lan_response.map((s) => ({
                label: `${s.ip} ${s.Denumire}`,
                value: Number(s.avg_value),
                color: ENDPOINT_COLORS.services,
                tip: `${Number(s.avg_value)}ms`,
              }))}
            />
          )
        }
      </OdbCard>

      {/* Totalul sta in capul cardului cu gogoasa - acolo unde si cardurile din
          catalog isi tin cifra - deci cele patru de sub el n-au nevoie de a
          cincea. */}
      <OdbCard module="opteemum" icon="fa-chart-pie" title="Alarms by category" w={1} h={2} id="topAlarmsChart" value={<span id="total-alarms">{alarms.total}</span>}>
        {(height) =>
          alarms.total === 0 ? (
            <ChartEmpty>No alarms at all in {period}.</ChartEmpty>
          ) : (
            <DoughnutChart
              height={height}
              centerText="Alarms"
              slices={[
                { label: 'Processor', value: alarms.processor, color: ENDPOINT_COLORS.processor },
                { label: 'Ram', value: alarms.ram, color: ENDPOINT_COLORS.ram },
                { label: 'Swap', value: alarms.swap, color: ENDPOINT_COLORS.swap },
                { label: 'Services', value: alarms.services, color: ENDPOINT_COLORS.services },
              ]}
            />
          )
        }
      </OdbCard>

      <AlarmCard label="Processor" icon="fa-microchip" id="processor-alarms" value={alarms.processor} total={alarms.total} color={ENDPOINT_COLORS.processor} />
      <AlarmCard label="Ram" icon="fa-memory" id="ram-alarms" value={alarms.ram} total={alarms.total} color={ENDPOINT_COLORS.ram} />
      <AlarmCard label="Swap" icon="fa-exchange-alt" id="swap-alarms" value={alarms.swap} total={alarms.total} color={ENDPOINT_COLORS.swap} />
      <AlarmCard label="Services" icon="fa-cogs" id="services-alarms" value={alarms.services} total={alarms.total} color={ENDPOINT_COLORS.services} />
    </OdbBoard>
  )
}

/**
 * Cate alarme a dat un fel de masuratoare, si cat inseamna din toate.
 *
 * Rigla e colorata IN CULOAREA FELULUI, nu in cea de stare. Nu e o abatere de la
 * regula zonei: cele patru culori - portocaliu procesor, verde ram, albastru
 * swap, bleu servicii - sunt cheia cu care se citesc si gogoasa, si stiva de
 * deasupra; stau in `EndpointCharts.tsx`, copiate din `endpoint_dashboard.js`. O
 * rigla gri aici ar fi rupt legatura dintre cifra si felia ei din grafic.
 *
 * NU primeste `tone`: n-avem prag peste care "trei alarme de procesor" e rau,
 * iar o culoare de stare pusa fara prag e o parere, nu o masuratoare.
 */
function AlarmCard({
  label,
  icon,
  id,
  value,
  total,
  color,
}: {
  label: string
  icon: string
  id: string
  value: number
  total: number
  color: string
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  return (
    <OdbCard module="opteemum" icon={icon} title={`${label} alarms`} w={1} h={1} value={<span id={id}>{value}</span>}>
      <>
        <span className="odb-scale">
          <span className="odb-scale-fill" style={{ width: `${pct}%`, background: color }}></span>
        </span>
        <div className="odb-ring-say">
          <span className="odb-of">
            {value} of {total} alarms · {pct}%
          </span>
        </div>
      </>
    </OdbCard>
  )
}

/**
 * Cat timp a stat fiecare aparat in alarma, pe cele patru feluri.
 *
 * In ecranul vechi era un tabel cu patru celule si cate un patratel colorat
 * langa fiecare ora. Aici e lista zonei (`.odb-rows`), cu bara din spatele
 * randului cat de mult inseamna aparatul fata de primul - aceeasi forma pe care
 * o au listele de pe celelalte tablouri, ca sa nu fie doua feluri de lista pe
 * acelasi portal.
 */
function AffectedRows({ rows, height, period }: { rows: EndpointTopAlarm[]; height: number; period: string }) {
  if (rows.length === 0) {
    return <ChartEmpty>No device spent time in alarm during {period}.</ChartEmpty>
  }
  const top = Math.max(1, ...rows.map((r) => Number(r.number)))

  return (
    <div style={{ height, overflowY: 'auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 11, color: '#5b6672' }}>
        <Legend color={ENDPOINT_COLORS.processor} label="processor" />
        <Legend color={ENDPOINT_COLORS.ram} label="ram" />
        <Legend color={ENDPOINT_COLORS.swap} label="swap" />
        <Legend color={ENDPOINT_COLORS.services} label="services" />
      </div>
      <div className="odb-rows">
        {rows.map((row) => (
          <div className="odb-row" key={row.device_id}>
            <span className="odb-row-fill" style={{ width: `${Math.round((Number(row.number) * 100) / top)}%` }}></span>
            <span className="odb-row-label">{row.Denumire}</span>
            {/*
              Cele patru timpuri stau pe UN rand, si de-aia scriu mai marunt
              decat cifra obisnuita a unei liste (16px, ingrosata).
              Masurat la 1280, unde cardul are 299px: cu marimea din foaie
              randul se rupea in doua, iar bara din spatele lui - inalta cat un
              rand - lasa a doua linie descoperita si lista arata zdrentuita.
            */}
            <span className="odb-row-value" style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', flex: '0 0 auto' }}>
              <Part minutes={Number(row.processor)} color={ENDPOINT_COLORS.processor} />
              <Part minutes={Number(row.ram)} color={ENDPOINT_COLORS.ram} />
              <Part minutes={Number(row.swap)} color={ENDPOINT_COLORS.swap} />
              <Part minutes={Number(row.services)} color={ENDPOINT_COLORS.services} />
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Un timp din rand: cifra si patratelul felului ei. Zero nu se scrie deloc. */
function Part({ minutes, color }: { minutes: number; color: string }) {
  if (!(minutes > 0)) return null
  return (
    <span style={{ marginLeft: 6, whiteSpace: 'nowrap' }}>
      {minutesLabel(minutes)}
      <span
        style={{ background: color, width: 7, height: 7, display: 'inline-block', marginLeft: 3, borderRadius: 2, verticalAlign: 'middle' }}
      ></span>
    </span>
  )
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ background: color, width: 18, height: 8, display: 'inline-block', borderRadius: 2 }}></span>
      {label}
    </span>
  )
}
